import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Predicate } from '../../../models/predicate.model';
import { useGlobalContext } from '../../../context/GlobalContext';
import { useTrilhaService } from '../../../services/trilha.service';
import {
  ITrilhaCleanDTO,
  ITrilhaFullDTO,
  IUnidadeCleanDTO,
} from '../../../models/happy-code-api.model';
import { PageableResponse } from '../../../models/response.model';
import { Operators } from '../../../enum/operators.enum';
import { useUnidadeService } from '../../../services/unidade.service';
import buildUserAccessPredicate from '../../../hooks/use-build-user-acess.hook';
import { getLocalStorageItem } from '../../../util/store';

export default function useAllCursos() {
  const { usuario, setIsGlobalLoading, unidade } = useGlobalContext();

  const [searchValue, setSearchValue] = useState('');

  const [trilhaFilter, setTrilhaFilter] = useState<ITrilhaCleanDTO>();

  const unidadeService = useUnidadeService();

  const trilhaService = useTrilhaService();

  const [trilhasData, setTrilhasData] = useState<ITrilhaCleanDTO[]>();


  const [paginatedTrilhas, setPaginatedTrilhas] =
    useState<PageableResponse<ITrilhaFullDTO>>();

  const addPredicateGenericOptions = useCallback(
    (page?: number): Predicate => {
      const predicate = new Predicate();

      predicate.addOption('ativo', true);
      predicate.addOption('cursos.ativo', true);

      if (page) predicate.setPage(page);

      if (searchValue)
        predicate.addOption('cursos.nome', searchValue, Operators.CONTAINS);

      return predicate;
    },
    [searchValue],
  );

  const findTipoUnidadeByUnidadeId = useCallback(async (): Promise<number> => {
    setIsGlobalLoading(true);
    return unidadeService.findById({ id: unidade?.id })
      .then(({ data }) => {
        setIsGlobalLoading(false);
        return data.tipoUnidade.id
      })
  }, [unidade]);

  const updateTipoUnidadeId = useCallback(async () => {
    const tipoUnidadeId = await findTipoUnidadeByUnidadeId();
    const predicate = addPredicateGenericOptions();
    const { unidadeTipoUnidadeCodigo } = getLocalStorageItem("@HappyCodeAcademy/login/unidade") as IUnidadeCleanDTO;

    if (tipoUnidadeId) {
      if (unidadeTipoUnidadeCodigo.toLowerCase() !== 'angola' && unidadeTipoUnidadeCodigo.toLowerCase() !== 'paraguai') {
        predicate.addOption('tipoUnidade.id', tipoUnidadeId);
      }
      else {
        predicate.addMultiOption('tipoUnidade.id', 1, Operators.OR);
        predicate.addMultiOption('tipoUnidade.id', 3, Operators.OR);
      }
    }


    Promise.all([
      findTrilhasFilter(predicate, unidade?.id),
      findPaginatedTrilhas(predicate, tipoUnidadeId)
    ]);
  }, [searchValue, trilhaFilter])

  const findTrilhasFilter = useCallback(
    async (predicate: Predicate, idUnidade: number) => {
      if (!usuario) return;
      if (trilhaFilter) predicate.addOption('id', trilhaFilter.id);

      const buildedPredicate = buildUserAccessPredicate(
        usuario,
        predicate
      );

      await trilhaService
        .findTrilhaFilterCustom(
          buildedPredicate
        )
        .then(({ data }) => setTrilhasData(data));
    },
    [setTrilhasData, trilhaFilter, usuario, setIsGlobalLoading],
  );

  const findPaginatedTrilhas = useCallback(
    async (predicate: Predicate, tipoUnidadeid: number) => {
      if (!unidade) return;

      await trilhaService
        .findFullCustom(predicate)
        .then(({ data }) => setPaginatedTrilhas(data));
    },
    [setPaginatedTrilhas, unidade, setIsGlobalLoading],
  );

  useEffect(() => {
    if (unidade?.id) {
      updateTipoUnidadeId();
    }
  }, [searchValue, unidade?.id, updateTipoUnidadeId]);

  const onPageChange = async (event: ChangeEvent<unknown>, page: number) => {
    const tipoUnidadeId = await findTipoUnidadeByUnidadeId();
    event.preventDefault();

    const predicate = addPredicateGenericOptions(page);

    findPaginatedTrilhas(predicate, tipoUnidadeId);
  };

  return {
    trilhasData,
    setSearchValue,
    searchValue,
    paginatedTrilhas,
    onPageChange,
    trilhaFilter,
    setTrilhaFilter,
  };
}
